import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS }                from '../typings/chart';
import { ChartPointModel }             from '../typings/intelligence.chart-point';
import { ChartSeriesModel }            from '../typings/intelligence.chart-series';
import { IntelligenceFinancialsModel } from '../typings/intelligence.financials';
import { IntelligenceFormatService }   from '../intelligence.format.service';
import { IntelligenceService }         from '../intelligence.service';

@Component({
  selector   : 'wor-intelligence-money-tab',
  template: require('./intelligence.money-tab.component.html')
})
export class MoneyTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  agingRows : Array<{ invoice_count : number; key : string; outstanding : number }> = [];
  payablesRows : Array<{ invoice_count : number; key : string; outstanding : number }> = [];
  mixPoints : Array<ChartPointModel> = [];
  paymentsPoints : Array<ChartPointModel> = [];
  revenuePoints : Array<ChartPointModel> = [];

  mixSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 2 ], key: 'labour_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.LABOUR') },
    { color: CHART_COLORS[ 1 ], key: 'parts_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.PARTS') },
    { color: CHART_COLORS[ 3 ], key: 'sublet_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.SUBLET') },
    { color: CHART_COLORS[ 4 ], key: 'other_revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER') }
  ];

  paymentsSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'payments', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.PAYMENTS') }
  ];

  revenueSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.REVENUE') },
    { color: CHART_COLORS[ 0 ], key: 'cost', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.COST') },
    { color: CHART_COLORS[ 2 ], key: 'gross_profit', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.GROSS_PROFIT') }
  ];

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  isAgingEmpty () : boolean {
    return !this.agingRows.some(row => row.outstanding > 0);
  }

  isMixEmpty () : boolean {
    return this.pointsEmpty(this.mixPoints);
  }

  isPayablesEmpty () : boolean {
    return !this.payablesRows.some(row => row.outstanding > 0);
  }

  isPaymentsEmpty () : boolean {
    return this.pointsEmpty(this.paymentsPoints);
  }

  isRevenueEmpty () : boolean {
    return this.pointsEmpty(this.revenuePoints);
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getFinancials()
    .then(( data : IntelligenceFinancialsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.revenuePoints = ( data.series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: {
          cost        : day.cost,
          gross_profit: day.gross_profit,
          revenue     : day.revenue
        }
      }));

      this.paymentsPoints = ( data.series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: { payments: day.payments }
      }));

      this.mixPoints = ( data.sales_mix_series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: {
          labour_revenue: day.labour_revenue,
          other_revenue : day.other_revenue,
          parts_revenue : day.parts_revenue,
          sublet_revenue: day.sublet_revenue
        }
      }));

      this.agingRows = this.buildAgingRows(data.receivables_aging || {});
      this.payablesRows = this.buildAgingRows(data.payables_aging || {});
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  private buildAgingRows ( aging : { [ bucket : string ] : { invoice_count : number; outstanding : number } } ) : Array<{ invoice_count : number; key : string; outstanding : number }> {
    return [ 'current', 'days_31_60', 'days_61_90', 'days_90_plus' ].map(key => ({
      invoice_count: aging[ key ] ? aging[ key ].invoice_count : 0,
      key,
      outstanding  : aging[ key ] ? aging[ key ].outstanding : 0
    }));
  }

  private pointsEmpty ( points : Array<ChartPointModel> ) : boolean {
    return !points.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }
}
